import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/lib/api';

export default function HonorarioModal({ open, onOpenChange, onSuccess, honorario = null }) {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [processos, setProcessos] = useState([]);

    const [formData, setFormData] = useState({
        processo_id: '',
        valor_proposto: '',
        valor_homologado: '',
        status_pagamento: 'pendente'
    });

    useEffect(() => {
        // Load available processos for the select box
        const loadProcessos = async () => {
            const resp = await api.processos.list();
            if (resp.data) {
                setProcessos(resp.data);
            }
        };
        if (open) loadProcessos();
    }, [open]);

    useEffect(() => {
        if (honorario) {
            setFormData({
                processo_id: honorario.processo_id?.toString() || '',
                valor_proposto: honorario.valor_proposto || '',
                valor_homologado: honorario.valor_homologado || '',
                status_pagamento: honorario.status_pagamento || 'pendente'
            });
        } else {
            setFormData({
                processo_id: '',
                valor_proposto: '',
                valor_homologado: '',
                status_pagamento: 'pendente'
            });
        }
    }, [honorario, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (name, val) => {
        setFormData(prev => ({ ...prev, [name]: val }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.processo_id) {
            toast({ title: "Atenção", description: "Selecione um processo.", variant: "destructive" });
            return;
        }

        setLoading(true);

        try {
            const payload = {
                processo_id: parseInt(formData.processo_id),
                valor_proposto: parseFloat(formData.valor_proposto || 0),
                valor_homologado: formData.valor_homologado ? parseFloat(formData.valor_homologado) : null,
                status_pagamento: formData.status_pagamento
            };

            let response;
            if (honorario && honorario.id) {
                response = await api.honorarios.update(honorario.id, payload);
            } else {
                response = await api.honorarios.create(payload);
            }

            if (response && !response.error) {
                toast({
                    title: "Sucesso!",
                    description: honorario ? "Honorário atualizado." : "Honorário cadastrado.",
                });
                onSuccess && onSuccess();
                onOpenChange(false);
            } else {
                toast({
                    title: "Erro ao salvar",
                    description: response?.error || "Ocorreu um erro.",
                    variant: "destructive"
                });
            }
        } catch (err) {
            toast({
                title: "Erro de Conexão",
                description: "Falha ao comunicar com o servidor.",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>{honorario ? 'Editar Honorário' : 'Novo Lançamento de Receita'}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    <div>
                        <label className="text-sm font-medium mb-1 block text-slate-700">Processo Vinculado *</label>
                        <Select
                            value={formData.processo_id}
                            onValueChange={(val) => handleSelectChange('processo_id', val)}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Selecione o Processo" />
                            </SelectTrigger>
                            <SelectContent>
                                {processos.map(p => (
                                    <SelectItem key={p.id} value={p.id.toString()}>
                                        {p.numero_cnj || p.numero} - {p.autor || (p.partes ? p.partes.autor : 'Múltiplos')}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block text-slate-700">Valor Proposto (R$)</label>
                            <Input
                                type="number"
                                step="0.01"
                                name="valor_proposto"
                                value={formData.valor_proposto}
                                onChange={handleChange}
                                placeholder="Ex: 5000.00"
                                required
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block text-slate-700">Valor Homologado (R$)</label>
                            <Input
                                type="number"
                                step="0.01"
                                name="valor_homologado"
                                value={formData.valor_homologado}
                                onChange={handleChange}
                                placeholder="Ex: 4500.00"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium mb-1 block text-slate-700">Status do Pagamento</label>
                        <Select
                            value={formData.status_pagamento}
                            onValueChange={(val) => handleSelectChange('status_pagamento', val)}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Selecione..." />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="pendente">Pendente</SelectItem>
                                <SelectItem value="parcial">Pago Parcialmente</SelectItem>
                                <SelectItem value="pago">Totalmente Pago</SelectItem>
                                <SelectItem value="cancelado">Cancelado</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <DialogFooter className="mt-6">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                            {loading ? 'Salvando...' : 'Salvar Receita'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
